
import React, { useState, FormEvent, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { COLORS } from '../../Styles/Colors';
import logo from '../../assets/images/AIMS_Logo_color_TM.png'; 
import Cookies from 'js-cookie';
import apiClient from '../../Services/ApiClient';
import MsalConfig from './Msal';

import { 
  Box, 
  Divider, 
  Typography 
} from '@mui/material';
import VerificationCodeInput from './VerificationCodeInput';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(135deg, ${COLORS.lightGray} 0%, #e0e0e0 100%);
  padding: 16px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const LoginForm = styled.form`
  background: white;
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 420px;
  text-align: center;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 1.5rem 1.25rem;
  }
`;

// Modify Title to be a div instead of h2 since it will contain an image
const Title = styled.div`
  margin-bottom: 1.5rem;
`;

// Add a styled component for the logo
const Logo = styled.img`
  max-width: 250px;
  height: auto;

  @media (max-width: 600px) {
    max-width: 180px;
  }
`;

// Rest of your styled components remain the same
const Input = styled.input`
  width: 100%;
  padding: 0.9rem;
  margin: 0.75rem 0;
  border: 1px solid ${COLORS.gray};
  border-radius: 6px;
  box-sizing: border-box;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  &:focus {
    border-color: ${COLORS.teal};
    outline: none;
    box-shadow: 0 0 4px rgba(0, 128, 128, 0.3);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.9rem;
  margin-top: 1.5rem;
  background-color: ${COLORS.teal};
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 500;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: ${COLORS.darkTeal};
  }
  &:disabled {
    background-color: ${COLORS.gray};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: ${COLORS.red};
  margin: 0.75rem 0;
  font-size: 0.9rem;
  font-weight: 500;
`;

const ForgotPasswordLink = styled.a`
  display: block;
  margin-top: 1rem;
  color: ${COLORS.teal};
  font-size: 0.9rem;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: ${COLORS.darkTeal};
  }
`;



const SuccessMessage = styled.div`
  color: green;
  margin: 10px 0;
  text-align: center;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 10px;
  
  &:disabled {
    color: #ccc;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const CountdownText = styled.p`
  color: #666;
  font-size: 14px;
  text-align: center;
  margin: 10px 0;
`;

interface LocationState {
  userId: string;
  phoneNumber: string;
}


const SMSVerification: React.FC = () => {
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resendCountdown, setResendCountdown] = useState<number>(60);
  const [canResend, setCanResend] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  useEffect(() => {
    if (!state?.userId || !state?.phoneNumber) {
      navigate('/');
    }
  }, [state, navigate]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendCountdown]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit verification code');
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiClient('/api/auth/verify-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          userId: state.userId,
          verificationCode 
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      Cookies.set('token', data.token, { expires: 1, sameSite: 'lax' });
      setSuccess('Verification successful! Redirecting...');
      
      setTimeout(() => {
        navigate('/home');
      }, 1500);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await apiClient('/api/auth/resend-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          userId: state.userId 
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend code');
      }

      setSuccess('New verification code sent!');
      setResendCountdown(60);
      setCanResend(false);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };


  if (!state?.userId || !state?.phoneNumber) {
    return null;
  }

  return (
    <LoginContainer>
      <LoginForm onSubmit={handleSubmit}>
        <Title>
          <Logo src={logo} alt="Company Logo" />
        </Title>
        
        <h2>Verify Your Phone Number</h2>
        <p>We've sent a 6-digit verification code to:</p>
        <p><strong>{state.phoneNumber}</strong></p>


        <Box sx={{ display: 'flex', justifyContent: 'center'}}>
                  <VerificationCodeInput code={verificationCode} setCode={setVerificationCode} />

        </Box>
        
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <Button type="submit" disabled={isLoading || verificationCode.length !== 6}>
          {isLoading ? 'Verifying...' : 'Verify Code'}
        </Button>
        
        {!canResend && (
          <CountdownText>
            Resend code in {resendCountdown} seconds
          </CountdownText>
        )}
        
        <ResendButton
          type="button"
          onClick={handleResendCode}
          disabled={!canResend || isLoading}
        >
          {canResend ? 'Resend Code' : 'Resend Code'}
        </ResendButton>
        
        <ResendButton
          type="button"
          onClick={() => navigate('/')}
          disabled={isLoading}
        >
          Back to Login
        </ResendButton>
      </LoginForm>
    </LoginContainer>
  );
};

export default SMSVerification;